import React, { Component } from 'react';
import { fromJS } from 'immutable';
import { camelCase } from 'lodash';
import findIndex from 'lodash/findIndex';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import makeTrashable from 'trashable';
import CommentList from 'components/CommentList';
import FadeElement from 'components/FadeElement';
import LoadingIndicator from 'components/LoadingIndicator';
import { isMobileDevice_DEPRECATED } from 'utils/mode';
import { wallPostShape } from 'utils/shapes';
import AuctionEndSoon from './AuctionEndSoon';
import AuctionFinished from './AuctionFinished';
import AuctionStart from './AuctionStart';
import DrawingFinished from './DrawingFinished';
import DrawingStartSoon from './DrawingStartSoon';
import FeedPinnedPosts from './FeedPinnedPosts';
import IFramePost from './IFramePost';
import Jackpot from './Jackpot';
import Post from './Post';
import TournamentFinished from './TournamentFinished';
import TournamentStartSoon from './TournamentStartSoon';
import WStoreReset from './WStoreReset';
import WheelWins from './WheelWins';
import './FeedList.css';

const splitPosts = memoizeOne(posts =>
  posts.reduce(
    (result, post) => {
      if (post.isPinned) {
        result.pinnedPosts.push(post);
      } else {
        result.unpinnedPosts.push(post);
      }
      return result;
    },
    { pinnedPosts: [], unpinnedPosts: [] }
  )
);

class FeedList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      commentInputShown: null,
      postsToRemove: [],
      commentInput: null,
      loadingMorePosts: null,
      editingPost: '',
      editingComment: ''
    };

    this.toggleComments = this.toggleComments.bind(this);
    this.openReply = this.openReply.bind(this);
    this.setCommentInput = this.setCommentInput.bind(this);
    this.setEditingComment = this.setEditingComment.bind(this);
    this.beginDeletePost = this.beginDeletePost.bind(this);
    this.deletePost = this.deletePost.bind(this);
    this.isEditingPostOrComment = this.isEditingPostOrComment.bind(this);
    this.beginEditPost = this.beginEditPost.bind(this);
    this.cancelEditPost = this.cancelEditPost.bind(this);
    this.saveEditPost = this.saveEditPost.bind(this);
  }

  componentWillUnmount() {
    if (this.state.loadingMorePosts && this.state.loadingMorePosts.trash) {
      this.state.loadingMorePosts.trash();
    }
  }

  toggleComments(id) {
    if (this.state.commentInputShown === id) {
      this.setState({
        commentInputShown: null
      });
    } else {
      this.setState({
        commentInputShown: id
      });
    }
  }

  setCommentInput(commentInput) {
    this.setState({ commentInput: commentInput });
  }

  setEditingComment(commentId) {
    this.setState({ editingComment: commentId });
  }

  openReply(id) {
    if (this.state.commentInputShown === id && this.state.commentInput) {
      this.state.commentInput.focus();
    } else {
      this.setState({
        commentInputShown: id
      });
    }
  }

  isNewPost(post) {
    return (
      findIndex(this.props.postsToAnimate, item => {
        return item.id === post.id;
      }) >= 0
    );
  }

  isPostRemoving(post) {
    return (
      findIndex(this.state.postsToRemove, item => {
        return item === post.id;
      }) >= 0
    );
  }

  loadMorePosts() {
    if (
      !this.props.loading &&
      !this.state.loadingMorePosts &&
      this.props.hasAnyMore
    ) {
      this.setState(state => ({
        loadingMorePosts: makeTrashable(this.props.loadMorePosts()).finally(
          () => {
            this.setState(state => ({ loadingMorePosts: null }));
          }
        )
      }));
    }
  }

  beginDeletePost(post) {
    this.setState({
      postsToRemove: fromJS(this.state.postsToRemove).push(post.id).toJS()
    });
  }

  deletePost(post) {
    this.props.onDeletePost(post);
  }

  isEditingPostOrComment() {
    return this.state.editingPost !== '' || this.state.editingComment !== '';
  }

  beginEditPost(post) {
    if (!this.isEditingPostOrComment()) {
      this.setState({
        editingPost: post.id
      });
    }
  }

  cancelEditPost() {
    this.setState({
      editingPost: ''
    });
  }

  saveEditPost(message, post) {
    if (message !== '' || post.imageUrl) {
      this.props.onSaveEditPost(post, message);
      this.setState({
        editingPost: ''
      });
    }
  }

  isLocationPreferred = locations => {
    const { preferredLocation } = this.props.user;
    return locations.includes(camelCase(preferredLocation));
  };

  getAuctionPost = post => {
    const { showLegacyFeed } = this.props;
    const { eventName, locations } = JSON.parse(post.message);

    if (!this.isLocationPreferred(locations)) return null;

    switch (eventName) {
      case 'start':
        return [
          <AuctionStart
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
          />
        ];
      case 'notifyEnd':
        return [
          <AuctionEndSoon
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
          />
        ];
      case 'finalize':
        return [
          <AuctionFinished
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
            toggleComments={this.toggleComments}
            onLikePost={this.props.onLikePost}
            onUnlikePost={this.props.onUnlikePost}
            onLockWallPost={this.props.onLockWallPost}
            onUnlockWallPost={this.props.onUnlockWallPost}
          />
        ];
      default:
        return null;
    }
  };

  getDrawingPost = post => {
    const { showLegacyFeed } = this.props;
    const { eventName, locations } = JSON.parse(post.message);
    const drawingStartSoonClassName = showLegacyFeed
      ? 'legacy-feed-list-drawing-start-soon'
      : 'feed-list-drawing-start-soon';
    const drawingFinishedClassName = showLegacyFeed
      ? 'legacy-feed-list-drawing-finished'
      : 'feed-list-drawing-finished';

    if (!this.isLocationPreferred(locations)) return null;

    switch (eventName) {
      case 'notifyStart':
        return [
          <DrawingStartSoon
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
          />,
          drawingStartSoonClassName
        ];
      case 'end':
        return [
          <DrawingFinished
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
            toggleComments={this.toggleComments}
            onLikePost={this.props.onLikePost}
            onUnlikePost={this.props.onUnlikePost}
            onLockWallPost={this.props.onLockWallPost}
            onUnlockWallPost={this.props.onUnlockWallPost}
          />,
          drawingFinishedClassName
        ];
      default:
        return null;
    }
  };

  getTournamentPost = post => {
    const { showLegacyFeed } = this.props;
    const { eventName, locations } = JSON.parse(post.message);
    const tournamentStartSoonClassName = showLegacyFeed
      ? 'legacy-feed-list-tournament-start-soon'
      : 'feed-list-tournament-start-soon';
    const tournamentFinishedClassName = showLegacyFeed
      ? 'legacy-feed-list-tournament-finished'
      : 'feed-list-tournament-finished';

    if (!this.isLocationPreferred(locations)) return null;

    switch (eventName) {
      case 'notifyStart':
        return [
          <TournamentStartSoon
            post={post}
            user={this.props.user}
            onDeletePost={this.beginDeletePost}
            showLegacyFeed={showLegacyFeed}
          />,
          tournamentStartSoonClassName
        ];
      case 'winners':
        return [
          <TournamentFinished
            post={post}
            user={this.props.user}
            showLegacyFeed={showLegacyFeed}
            onDeletePost={this.beginDeletePost}
            toggleComments={this.toggleComments}
            onLikePost={this.props.onLikePost}
            onUnlikePost={this.props.onUnlikePost}
            onLockWallPost={this.props.onLockWallPost}
            onUnlockWallPost={this.props.onUnlockWallPost}
          />,
          tournamentFinishedClassName
        ];
      default:
        return null;
    }
  };

  getJackpotPost = post => {
    return [
      <Jackpot
        post={post}
        user={this.props.user}
        onDeletePost={this.beginDeletePost}
        showLegacyFeed={this.props.showLegacyFeed}
      />
    ];
  };

  getWStorePost = post => {
    return [
      <WStoreReset
        post={post}
        user={this.props.user}
        onDeletePost={this.beginDeletePost}
      />
    ];
  };

  getWheelWinsPost = post => {
    return [
      <WheelWins
        post={post}
        user={this.props.user}
        onDeletePost={this.beginDeletePost}
      />
    ];
  };

  getIFramePost = post => {
    return [
      <IFramePost
        post={post}
        user={this.props.user}
        onDeletePost={this.beginDeletePost}
        toggleComments={this.toggleComments}
        onPinWallPost={this.props.onPinWallPost}
        onUnpinWallPost={this.props.onUnpinWallPost}
        onLockWallPost={this.props.onLockWallPost}
        onUnlockWallPost={this.props.onUnlockWallPost}
        onLikePost={this.props.onLikePost}
        onUnlikePost={this.props.onUnlikePost}
      />
    ];
  };

  getSystemPost = post => {
    const { systemCode } = JSON.parse(post.message);

    switch (systemCode) {
      case 'AUCTION':
        return this.getAuctionPost(post);
      case 'JACKPOTS':
        return this.getJackpotPost(post);
      case 'WSTORE':
        return this.getWStorePost(post);
      case 'WHEELWINS':
        return this.getWheelWinsPost(post);
      case 'TOURNAMENT':
        return this.getTournamentPost(post);
      case 'DRAWING':
        return this.getDrawingPost(post);
      case 'IFRAME':
        return this.getIFramePost(post);
      default:
        return null;
    }
  };

  wrapPost = (post, postElement) => {
    return (
      <React.Fragment>
        <FadeElement
          onPostAnimationComplete={() => {
            this.props.onPostAnimationComplete(post);
          }}
          onRemoveComplete={() => {
            this.deletePost(post);
          }}
          in={!this.isNewPost(post)}
          removing={this.isPostRemoving(post)}
        >
          {postElement}
        </FadeElement>
        <CommentList
          addComment={(postId, message) => {
            if (this.props.addComment(postId, message)) {
              this.toggleComments(postId);
              return true;
            }
            return false;
          }}
          commentInputShown={this.state.commentInputShown}
          post={post}
          editingComment={this.state.editingComment}
          setEditingComment={this.setEditingComment}
          isEditingPostOrComment={this.isEditingPostOrComment}
          onSaveEditComment={this.props.onSaveEditComment}
          onDeleteComment={this.props.onDeleteComment}
          user={this.props.user}
          loadMoreComments={this.props.loadMoreComments}
          commentsToAnimate={this.props.commentsToAnimate}
          onCommentAnimationComplete={this.props.onCommentAnimationComplete}
          onLikeComment={this.props.onLikeComment}
          onUnlikeComment={this.props.onUnlikeComment}
          openReply={this.openReply}
          setCommentInput={this.setCommentInput}
          showLegacyFeed={this.props.showLegacyFeed}
        />
      </React.Fragment>
    );
  };

  renderPost = (post, wrapperRenderFn = undefined) => {
    const postProps = {
      user: this.props.user,
      editingPost: this.state.editingPost,
      isEditingPostOrComment: this.isEditingPostOrComment,
      onDeletePost: this.beginDeletePost,
      onEditPost: this.beginEditPost,
      onCancelEditPost: this.cancelEditPost,
      onSaveEditPost: this.saveEditPost,
      toggleComments: this.toggleComments,
      onPinWallPost: this.props.onPinWallPost,
      onUnpinWallPost: this.props.onUnpinWallPost,
      onLockWallPost: this.props.onLockWallPost,
      onUnlockWallPost: this.props.onUnlockWallPost,
      onLikePost: this.props.onLikePost,
      onUnlikePost: this.props.onUnlikePost,
      showLegacyFeed: this.props.showLegacyFeed
    };

    let wrapperClasses = '';
    let postElement = null;
    if (post.isSystemPost) {
      [postElement, wrapperClasses = ''] = this.getSystemPost(post) || [];
      const feedListSystemPostType = this.props.showLegacyFeed
        ? 'legacy-feed-list-system-post'
        : 'feed-list-system-post';
      wrapperClasses += ` ${feedListSystemPostType}`;
    } else {
      const feedListPostType = this.props.showLegacyFeed
        ? 'legacy-feed-list-post'
        : 'feed-list-post';
      wrapperClasses = ` ${feedListPostType}`;
      postElement = <Post post={post} {...postProps} />;
    }

    if (!postElement) {
      return null;
    }

    postElement = this.wrapPost(post, postElement);

    if (wrapperRenderFn) {
      postElement = wrapperRenderFn(postElement, wrapperClasses);
    }

    return postElement;
  };

  render() {
    const { showLegacyFeed, posts, user, loading } = this.props;

    if (!posts.length || !user) {
      if (loading) {
        return <LoadingIndicator expand />;
      } else {
        return (
          <div className="text-center feed-alert">
            No posts available at this time.
          </div>
        );
      }
    }

    const mobile = isMobileDevice_DEPRECATED();
    const { pinnedPosts, unpinnedPosts } = splitPosts(posts);
    const feedType = showLegacyFeed ? 'legacy-feed-list' : 'feed-list';

    return (
      <InfiniteScroll
        loadMore={this.loadMorePosts.bind(this)}
        hasMore={this.props.hasAnyMore}
        loader={<LoadingIndicator key="wall-load-more" />}
        useWindow={false}
        threshold={mobile ? 1000 : 250}
        role="feed"
        aria-label="Community Wall Message Feed"
      >
        <ul className={feedType}>
          {pinnedPosts.length > 0 && (
            <li key="pinned">
              <FeedPinnedPosts
                pinnedPosts={pinnedPosts}
                renderPostFn={this.renderPost}
                showLegacyFeed={showLegacyFeed}
              />
            </li>
          )}
          {unpinnedPosts.map((post, postIndex) =>
            this.renderPost(post, (postElement, wrapperClasses) => (
              <li
                className={wrapperClasses}
                key={post.id}
                role="article"
                aira-posinset={postIndex + 1}
                aria-setsize={unpinnedPosts.length}
              >
                {postElement}
              </li>
            ))
          )}
        </ul>
      </InfiniteScroll>
    );
  }
}

FeedList.propTypes = {
  addComment: PropTypes.func,
  posts: PropTypes.arrayOf(wallPostShape),
  commentInputShown: PropTypes.number,
  onSaveEditPost: PropTypes.func,
  onSaveEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  onDeletePost: PropTypes.func,
  hasAnyNext: PropTypes.bool,
  loadMorePosts: PropTypes.func,
  loadMoreComments: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.instanceOf(Error),
  postsToAnimate: PropTypes.array,
  commentsToAnimate: PropTypes.array,
  onPostAnimationComplete: PropTypes.func,
  onCommentAnimationComplete: PropTypes.func,
  onPinWallPost: PropTypes.func,
  onUnpinWallPost: PropTypes.func,
  onLockWallPost: PropTypes.func,
  onUnlockWallPost: PropTypes.func,
  onLikePost: PropTypes.func,
  onUnlikePost: PropTypes.func,
  onLikeComment: PropTypes.func,
  onUnlikeComment: PropTypes.func,
  showLegacyFeed: PropTypes.bool
};

export default FeedList;
